import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import { setStoryVisibility } from '@/modules/databaseService/scenes';
import type { ChosenVisibility } from '@/infrastructure/api/v1/mappers/stories';
import type { SceneOrigin } from '@/types/sceneTypes';

/**
 * The scene page's public / unlisted switch: shows the change at once and puts it back if the server refuses.
 * One save at a time: the server applies overlapping saves in lock order, not click order.
 */
export function useStoryVisibilitySwitch(
  sceneOrigin: SceneOrigin | null,
  setSceneOrigin: Dispatch<SetStateAction<SceneOrigin | null>>,
) {
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const onChange = useCallback(
    async (next: ChosenVisibility) => {
      if (!sceneOrigin || inFlight.current || next === sceneOrigin.visibility) return;
      inFlight.current = true;
      setSaving(true);
      const previous = sceneOrigin.visibility;
      setSceneOrigin((prev) => (prev ? { ...prev, visibility: next } : prev));
      try {
        const { error } = await setStoryVisibility(sceneOrigin.sceneOriginId, next);
        if (error) {
          setSceneOrigin((prev) => (prev ? { ...prev, visibility: previous } : prev));
          toast.error(error.message || "Couldn't change who can find this story");
          return;
        }
        toast.success(next === 'public' ? 'Your story is public' : 'Your story is unlisted');
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [sceneOrigin, setSceneOrigin],
  );

  return { onChange, saving };
}
